import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * Номер чужой записи ссылкой: маршрут в строке заявки, заявка в составе маршрута, талон
 * заказчика в журнале листов. Ведёт на вкладку, где эту запись показывают, и просит открыть её
 * карточку (`?tab=…&open=…`).
 *
 * Настоящая ссылка, а не кнопка с переходом, по трём причинам. Её открывают средним щелчком и
 * Ctrl'ом — соседней вкладкой браузера, не теряя списка, из которого пришли. И клик по ней не
 * открывает заодно карточку самой строки: `DataTable` отдаёт строку нажатию только там, где под
 * курсором не оказалось ссылки или кнопки (см. `opensRow`). Третья причина появилась вместе с
 * `onActivate`: обычный клик по номеру больше никуда не уводит, а открывает запись окном поверх
 * той страницы, где стоишь, — и ровно поэтому адрес обязан остаться настоящим. Ссылку, ставшую
 * кнопкой, соседней вкладкой уже не откроешь, а пользуются этим постоянно.
 *
 * Отсюда правило перехвата: гасим только левый клик без модификаторов. Ctrl, Meta, Shift и Alt
 * оставляем браузеру — это его способы открыть адрес вкладкой, окном или сохранить его, и подмена
 * их окном была бы поломкой ссылки. Средний щелчок ловить и не пытаемся: `onClick` по нему
 * браузер не зовёт вовсе — достаточно ему не мешать, а `e.button === 0` отсекает остальные кнопки
 * там, где событие всё же дойдёт.
 *
 * `to === null` — и `onActivate` не зовётся вовсе: номер остаётся прежним текстом. Это про права,
 * а не про удобство. Окно, открытое там, где ссылки не должно быть, — та же дыра, что и ссылка на
 * запретный раздел, только без подсветки: показали бы то, чего роли не положено, и без адреса,
 * по которому это заметно. Право спрашивается один раз, при вычислении адреса — обёрткой в слайсе
 * той записи, куда ведёт номер, — и второй двери мимо него здесь нет.
 */
export function EntityLink({
  to,
  title,
  onActivate,
  children,
}: {
  /**
   * Куда вести. `null` — некуда: раздел или вкладка этой роли не положены (так отвечает обёртка
   * перехода в слайсе записи), и номер остаётся прежним текстом (и никакого `onActivate`). Проверка
   * здесь, а не у каждого вызова, потому что мест вызова много, а условие у них одно.
   */
  to: string | null;
  /** Подсказка наведения: чем этот переход кончится — «Открыть маршрут», «Открыть заявку». */
  title?: string;
  /**
   * Чем заменить обычный переход. Передан — левый клик без модификаторов гасится
   * (`preventDefault`) и вместо ухода на другую вкладку портала открывает окно поверх текущей
   * страницы. Не передан — всё как раньше, обычный переход: места вызова, у которых окна нет,
   * ничего о нём и не знают.
   */
  onActivate?: () => void;
  children: ReactNode;
}) {
  if (!to) return <>{children}</>;
  return (
    <Link
      to={to}
      title={title}
      className="entity-link"
      onClick={
        onActivate &&
        ((e) => {
          if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onActivate();
        })
      }
    >
      {children}
    </Link>
  );
}
